/** Shared context/mutation wiring for TaskListBase controller. */
// @ts-check
import { useMemo, useState, useCallback, useRef } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import useTaskSelectionAndFocus from '@/features/tasks/hooks/list/use-list-selection';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import { useFocusContext } from '@/features/tasks/context/FocusContextExport';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';

export function useTaskListBaseSharedState({
  tasks,
  selectedTaskIds,
  onSelectionChange,
}) {
  const { allTasks, tasksMap, user, duplicateOrderKeyTaskIds } =
    useTasksContext();
  const {
    handleToggleComplete,
    deleteTask: handleDeleteTask,
    updateTask,
    rebalanceOrderKeys,
    triggerFollowUp,
    handleStartFocus,
  } = useTaskMutationsContext();
  const { activeFocusTaskId, stopFocus } = useFocusContext();
  const { addTask } = useAddTask();

  const selectedIndex = useMemo(() => {
    if (selectedTaskIds.length === 0) return -1;
    return tasks.findIndex((t) => t.id === selectedTaskIds.at(-1));
  }, [selectedTaskIds, tasks]);
  const [isFocused, setIsFocused] = useState(false);
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const newTaskSessionRef = useRef(0);
  const cancelNewTaskInputRef = useRef(() => {});

  const {
    taskListRef,
    selectSingleTask,
    toggleTaskInSelection,
    selectRangeTasks,
    deselectAllTasks: deselectAllTasksHook,
    focusContainer,
  } = useTaskSelectionAndFocus({ selectedTaskIds, onSelectionChange });

  const deselectAllTasks = useCallback(() => {
    deselectAllTasksHook();
    setIsBulkSelectMode(false);
    focusContainer();
  }, [deselectAllTasksHook, focusContainer]);

  return {
    allTasks,
    tasksMap,
    user,
    duplicateOrderKeyTaskIds,
    handleToggleComplete,
    handleDeleteTask,
    updateTask,
    rebalanceOrderKeys,
    triggerFollowUp,
    handleStartFocus,
    stopFocus,
    activeFocusTaskId,
    addTask,
    selectedIndex,
    isFocused,
    setIsFocused,
    isBulkSelectMode,
    setIsBulkSelectMode,
    newTaskSessionRef,
    cancelNewTaskInputRef,
    taskListRef,
    selectSingleTask,
    toggleTaskInSelection,
    selectRangeTasks,
    focusContainer,
    deselectAllTasks,
  };
}
